import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, Calendar } from 'lucide-react';
import { getUID } from '@/utils';
import DatePicker from '@/components/common/DatePicker/DatePicker';
import styles from './BetHistoryTab.module.css';

/**
 * BetHistoryTab Component
 * Displays bet history with search and filters
 */
const BetHistoryTab = () => {
  const [betHistory, setBetHistory] = useState({ transactions: [], pagination: {} });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: '',
    transactionType: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchBetHistory = useCallback(async (page = 1, limit = 10) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page,
        limit,
        ...Object.fromEntries(
          Object.entries({
            date_from: filters.dateFrom,
            date_to: filters.dateTo,
            transaction_status: filters.status,
            transaction_type: filters.transactionType
          }).filter(([_, value]) => value !== '')
        )
      });
      
      if (debouncedSearchTerm) {
        queryParams.append('search', debouncedSearchTerm);
      }

      const id = getUID();
      const response = await fetch(
        `https://api.jackopot.com/betHistory/transactions/user/${id}?${queryParams}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch bet history');
      }
      
      const data = await response.json();
      setBetHistory(data);
    } catch (error) {
      console.error('Error fetching bet history:', error);
      setBetHistory({ transactions: [], pagination: {} });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, filters]);

  useEffect(() => {
    fetchBetHistory(currentPage);
  }, [fetchBetHistory, currentPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, date) => {
    const dateString = date ? date.toISOString().split('T')[0] : '';
    setFilters(prev => ({ ...prev, [name]: dateString }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchBetHistory(1);
  };

  const resetFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      status: '',
      transactionType: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch(status?.toUpperCase()) {
      case 'ERROR': return 'var(--color-error)';
      case 'SUCCESS': return 'var(--color-success)';
      case 'PENDING': return 'var(--color-live)';
      default: return 'var(--color-text-secondary)';
    }
  };

  return (
    <div className={styles.container}>
      {/* Search and Filter Bar */}
      <div className={styles.searchBar}>
        <form className={styles.searchForm} onSubmit={handleSearch}>
          <Search className={styles.searchIcon} size={16} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by game title, transaction ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </form>
        <button
          className={styles.filterButton}
          onClick={() => setShowFilters(!showFilters)}
          aria-label="Toggle filters"
        >
          <Filter size={16} />
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className={styles.filtersContainer}>
          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <label className={styles.label}>From Date</label>
              <DatePicker
                selected={filters.dateFrom ? new Date(filters.dateFrom) : null}
                onChange={(date) => handleDateChange('dateFrom', date)}
                placeholderText="Select from date"
                maxDate={filters.dateTo ? new Date(filters.dateTo) : new Date()}
                dateFormat="MM/dd/yyyy"
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.label}>To Date</label>
              <DatePicker
                selected={filters.dateTo ? new Date(filters.dateTo) : null}
                onChange={(date) => handleDateChange('dateTo', date)}
                placeholderText="Select to date"
                minDate={filters.dateFrom ? new Date(filters.dateFrom) : null}
                maxDate={new Date()}
                dateFormat="MM/dd/yyyy"
              />
            </div>
          </div>
          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <label className={styles.label}>Status</label>
              <select
                name="status"
                className={styles.filterSelect}
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                <option value="SUCCESS">Success</option>
                <option value="ERROR">Error</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.label}>Transaction Type</label>
              <select
                name="transactionType"
                className={styles.filterSelect}
                value={filters.transactionType}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                <option value="BET">Bet</option>
                <option value="WIN">Win</option>
                <option value="REFUND">Refund</option>
              </select>
            </div>
          </div>
          <div className={styles.filterActions}>
            <button className={styles.applyButton} onClick={applyFilters}>
              Apply Filters
            </button>
            <button className={styles.resetButton} onClick={resetFilters}>
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loader}>
            <span className={styles.loaderText}>loading</span>
            <span className={styles.load}></span>
          </div>
        </div>
      ) : betHistory.transactions?.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No bet history found</p>
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Game</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {betHistory.transactions.map((bet) => (
                  <tr key={bet.id}>
                    <td>
                      <div className={styles.gameInfo}>
                        <div className={styles.gameTitle}>{bet.game_title}</div>
                        <div className={styles.vendorName}>{bet.game_vendor}</div>
                      </div>
                    </td>
                    <td>
                      <span 
                        className={styles.amount}
                        style={{ 
                          color: bet.transaction_type === 'WIN' 
                            ? 'var(--color-success)' 
                            : bet.transaction_type === 'LOSS'
                            ? 'var(--color-error)'
                            : 'var(--color-text-primary)'
                        }}
                      >
                        {bet.transaction_type === 'WIN' ? '+' : bet.transaction_type === 'LOSS' ? '-' : ''}
                        {bet.amount} {bet.currency_code}
                      </span>
                    </td>
                    <td>
                      <span className={styles.transactionType}>{bet.transaction_type}</span>
                    </td>
                    <td>
                      <span className={styles.transactionDate}>
                        {formatDate(bet.transaction_timestamp)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {betHistory.pagination && betHistory.pagination.totalPages > 1 && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationInfo}>
                Showing {betHistory.transactions.length} of {betHistory.pagination.totalCount} entries
              </div>
              <div className={styles.paginationControls}>
                <button
                  className={styles.paginationButton}
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </button>
                <span className={styles.pageInfo}>
                  Page {currentPage} of {betHistory.pagination.totalPages}
                </span>
                <button
                  className={styles.paginationButton}
                  disabled={currentPage === betHistory.pagination.totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BetHistoryTab;
